// THE HULL AND ITS READOUT: the harvesting looks that ride the hull, clear of
// the zone astern where the ten Dredger marks live (R1120, mockup round 055
// group c).
//
// One entry per look id of `labels.toml gather_styles` that this file owns:
// `hold-ring` (card 11) and `work-lights` (card 12). `gather-styles.ts`
// prefers this map over the AUTO look, so a look that is not here yet draws
// the AUTO look and the map fills in one card at a time.
//
// **PENDING names every card of this file that is not built yet.** An id must
// stand in `RECIPES` or in `PENDING`, and `gather-recipes-hull.test.ts` fails
// when one stands in neither. An agent that builds a card moves its id out of
// `PENDING`, adds its row to `data/presentation/gather.toml
// [[gather.look]]`, and touches no other file.
//
// **`hold-ring` takes ONE mark away from the fleet layer.** It draws the
// laden ring as the gauge of the hold, so its recipe sets `ownsLadenRing` and
// the fleet layer leaves that ring out on that hull. The card of the mockup
// round replaced the cargo lane as well, but the live lane is always on
// (R1122), so no look may hide it and the recipe interface holds no switch
// for it. On the run home the plain laden ring comes back, as the card says.
//
// **`hold-ring` must read `c.fill` before it gauges.** The hold is −1 on a
// faction hull, because the wire carries no hold for one (R1121). A card that
// gauged an unread hold would draw a full ring on every squadmate. The fleet
// layer draws no laden ring on such a hull either, so the look draws nothing
// at all there.
//
// This is a leaf (`leaves.test.ts`): it imports the kit and the generated
// pack, and never an engine.

import {
  TAU, GATHER_WHITE, PALE_INK, arcOf, clamp01, disc, glow, inHull, lighten, lotBeat,
  rigEnv, ring, type GatherCtx, type GatherRecipe, type GatherRow, type Pt,
} from './gather-recipe-kit';

// ---------------------------------------------------------------------------
// The proportions
// ---------------------------------------------------------------------------
//
// Every figure of a card stands in `data/presentation/gather.toml`. A card
// that spends more figures than the fourteen fields hold states the rest as a
// SHARE of a figure the row carries, which is what a proportion is: the
// quotient says what the card wrote, and it holds at any figure.

/** Twelve o'clock: where the gauge of the hold starts. */
const TOP = -Math.PI / 2;

/** hold-ring: the SHAFT head's radius, as a share of the gather head's. The
 *  card writes 2.1 against 1.5, because a hollow head must read as wide as a
 *  filled one. */
const HEAD_SHAFT_R = 2.1 / 1.5;

/** hold-ring: how far the SHAFT head swells at the beat of a lot, as a share
 *  of the gather head's swell. The card writes 0.7 against 1.3. */
const HEAD_SHAFT_PULSE = 0.7 / 1.3;

/** hold-ring: the SHAFT head's alpha, and how much brighter it burns at the
 *  beat, as shares. The card writes 0.6 + 0.35 against the head's 0.75. */
const HEAD_SHAFT_ALPHA = 0.6 / 0.75;
const HEAD_SHAFT_LIFT = 0.35 / 0.6;

/** hold-ring: how much brighter the gather head burns at the beat of a lot,
 *  as a share of its own alpha. The card writes 0.75 + 0.25. */
const HEAD_LIFT = 0.25 / 0.75;

/** hold-ring: the halo outside the gather head. Its alpha is a share of the
 *  head's, its stroke a share of the ring's, and under `HALO_FLOOR` of the
 *  beat it does not draw at all. */
const HALO_ALPHA = 0.4 / 0.75;
const HALO_STROKE = 0.8;
const HALO_FLOOR = 0.05;

/** hold-ring: how far toward white the cargo brown goes for the head, for
 *  the ghost ring of a full hold, and for the ring itself at the flash. */
const LIFT_HEAD = 0.55;
const LIFT_GHOST = 0.5;
const LIFT_FULL = 0.6;

/** hold-ring: how much heavier and how much brighter the plain ring rides at
 *  the flash of a full hold, as shares of its own stroke and alpha. The card
 *  writes 1 + 1.4 f and 0.7 + 0.3 f. */
const RING_FULL_SWELL = 1.4;
const RING_FULL_LIFT = 0.3 / 0.7;

/** hold-ring: the ghost ring's alpha, as a share of the plain ring's. */
const GHOST_ALPHA = 0.55 / 0.7;

/** work-lights: where in a lot a lamp blinks and how long the blink lasts,
 *  as shares of the lot. The shaft blinks earlier and shorter, so the two
 *  states read apart by rhythm and by count and never by colour (R715). */
const LAMP_AT = 0.78;
const LAMP_FOR = 0.12;
const LAMP_SHAFT_AT = 0.4;
const LAMP_SHAFT_FOR = 0.08;

/** work-lights: how hard the full hold burns both lamps steady, and the lit
 *  share under which a lamp shows its housing alone. */
const LAMP_STEADY_SNAP = 4;
const LAMP_FLOOR = 0.02;

/** work-lights: how far toward white the cargo brown goes for the light of a
 *  work lamp, which is warm and not of the commodity. */
const LAMP_TINT = 0.5;

/** The reused scratch of this file (R131). */
const P0: Pt = { x: 0, y: 0 };

// ---------------------------------------------------------------------------
// The pieces
// ---------------------------------------------------------------------------

/**
 * work-lights: ONE lamp at the hull point `(x, y)`, lit by `lit` and rigged
 * for the cut by `rig`. It is `cLamp` of the card.
 *
 * The housing always stands while the cut runs, so a still shows where the
 * lamps are even in the dark part of a blink.
 */
function lamp(
  c: GatherCtx, r: GatherRow, x: number, y: number, lit: number, rig: number,
): void {
  const p = inHull(c, x, y, P0);
  const px = p.x, py = p.y;
  ring(c, px, py, r.rPx, r.widthPx, PALE_INK, r.alpha * rig);
  if (lit <= LAMP_FLOOR) return;
  glow(c, px, py, r.lengthPx, lighten(c.cargo, LAMP_TINT), r.alpha2 * lit);
  disc(c, px, py, r.r2Px, GATHER_WHITE, r.alpha3 * lit);
}

// ---------------------------------------------------------------------------
// The recipes
// ---------------------------------------------------------------------------

export const RECIPES: Readonly<Record<string, GatherRecipe>> = {
  /**
   * Card 11, HOLD RING.
   *
   * The laden ring becomes the gauge of the hold: an arc sweeps clockwise
   * from twelve o'clock to the fill, and a bright head at its end pulses one
   * time per lot. On a full hold the ring closes, flashes one time, and
   * rides home as the plain laden ring. In the shaft the head is hollow and
   * the pulse is slow.
   */
  'hold-ring': {
    ownsLadenRing: true,
    over(c: GatherCtx): void {
      if (c.vis <= 0) return;
      // The hold is −1 where the viewer cannot read it (R1121). The fleet
      // layer draws no laden ring on such a hull either, so the gauge draws
      // nothing rather than a full ring on every squadmate.
      if (c.fill < 0) return;
      const r = c.row;
      const x = c.hx, y = c.hy;
      if (!c.atRest || c.fill >= 1) {
        // The full hold and the run home: the plain laden ring, which
        // flashes one time as the hold fills and takes a ghost ring with it.
        if (!c.laden) return;
        const f = c.full;
        ring(c, x, y, r.rPx, r.width2Px * (1 + RING_FULL_SWELL * f),
          lighten(c.cargo, LIFT_FULL * f), r.alpha3 * (1 + RING_FULL_LIFT * f));
        if (f > 0) {
          ring(c, x, y, r.rPx + r.driftPx * (1 - f), r.width2Px,
            lighten(c.cargo, LIFT_GHOST), r.alpha3 * GHOST_ALPHA * f);
        }
        return;
      }
      // The gauge: a ground ring, the arc of the hold, and the head.
      ring(c, x, y, r.rPx, r.width2Px, c.cargo, r.alpha2);
      const hr = c.shaft ? r.r2Px * HEAD_SHAFT_R : r.r2Px;
      const end = TOP + TAU * c.fill;
      // A hollow head sits ON the arc, so the shaft arc stops short of it by
      // the head's own radius. The filled head of a gather needs no gap.
      const gap = c.shaft ? hr / r.rPx : 0;
      if (c.fill > gap / TAU) {
        arcOf(c, x, y, r.rPx, r.widthPx, TOP, TAU * c.fill - gap, c.cargo, r.alpha);
      }
      const hx = x + Math.cos(end) * r.rPx, hy = y + Math.sin(end) * r.rPx;
      const p = lotBeat(c) * c.work;
      const tint = lighten(c.cargo, LIFT_HEAD);
      if (c.shaft) {
        ring(c, hx, hy, hr + r.lengthPx * HEAD_SHAFT_PULSE * p, r.width2Px, tint,
          r.alpha4 * HEAD_SHAFT_ALPHA * (1 + HEAD_SHAFT_LIFT * p));
        return;
      }
      const hrp = hr + r.lengthPx * p;
      disc(c, hx, hy, hrp, tint, r.alpha4 * (1 + HEAD_LIFT * p));
      if (p > HALO_FLOOR) {
        ring(c, hx, hy, hrp + r.length2Px, r.width2Px * HALO_STROKE, tint,
          r.alpha4 * HALO_ALPHA * p);
      }
    },
  },

  /**
   * Card 12, WORK LIGHTS.
   *
   * Two small lamps abeam of the hull, clear of the field and of every mark,
   * blink in turn, port then starboard, one blink per lot. In the shaft the
   * Dredger shows the port lamp alone, and the blink is short and slow. On a
   * full hold both lamps burn steady for the beat, and then they go out.
   */
  'work-lights': {
    over(c: GatherCtx): void {
      const rig = rigEnv(c);
      if (rig <= 0 || c.vis <= 0) return;
      const r = c.row;
      const n = c.shaft ? r.count2 : r.count;
      if (n < 1) return;
      const at = c.shaft ? LAMP_SHAFT_AT : LAMP_AT;
      const span = c.shaft ? LAMP_SHAFT_FOR : LAMP_FOR;
      const blink = clamp01((at - c.lot) / span) * c.work;
      const steady = clamp01(c.full * LAMP_STEADY_SNAP);
      // The lamps blink in turn, port first. A shaft lights the port lamp
      // alone, which is how it reads apart from a gather in a still.
      const port = c.shaft || c.lotN % 2 === 0;
      lamp(c, r, -r.driftPx, -r.length2Px, Math.max(steady, port ? blink : 0), rig);
      if (n < 2) return;
      lamp(c, r, -r.driftPx, r.length2Px, Math.max(steady, port ? 0 : blink), rig);
    },
  },
};

/**
 * The cards of this file that wait for their own recipe, in card order.
 *
 * The table is EMPTY: `hold-ring` (card 11) and `work-lights` (card 12) are
 * both built.
 */
export const PENDING: readonly string[] = [];
